import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { SettingsHeader } from "@/components/general/Settings/SettingsHeader";
import { SettingsActions } from "@/components/general/Settings/SettingsActions";
import { SettingsList } from "@/components/general/Settings/SettingsList";
import { Multiplier, multipliersQueryOptions, flattenMultipliers } from "@/api/multipliers";

export default function MultiplierSettingsPage() {
    const [editedMultipliers, setEditedMultipliers] = useState<Map<number, Partial<Multiplier>>>(new Map());

    const { data: multipliersData, isLoading, error, refetch, isFetching } = useQuery(multipliersQueryOptions());

    const multipliers = useMemo(() => {
        return multipliersData ? flattenMultipliers(multipliersData) : [];
    }, [multipliersData]);

    const handleRefresh = () => {
        setEditedMultipliers(new Map());
        refetch();
    };

    const hasAnyChanges = useMemo(() => {
        return multipliers.some((m) => {
            const edited = editedMultipliers.get(m.id);
            if (!edited) return false;

            return (
                (edited.weight !== undefined && edited.weight !== m.weight) ||
                (edited.maxPoints !== undefined && edited.maxPoints !== m.maxPoints) ||
                (edited.isActive !== undefined && edited.isActive !== m.isActive)
            );
        });
    }, [multipliers, editedMultipliers]);

    const isActuallyLoading = isLoading || isFetching;

    return (
        <>
            <SettingsHeader
                isLoading={isActuallyLoading}
                hasChanges={hasAnyChanges}
                onRefresh={handleRefresh}
                multipliers={multipliers}
                editedMultipliers={editedMultipliers}
                setEditedMultipliers={setEditedMultipliers}
            />

            <SettingsList
                multipliersData={multipliersData}
                editedMultipliers={editedMultipliers}
                setEditedMultipliers={setEditedMultipliers}
                isLoading={isActuallyLoading}
                error={error}
                onRefresh={handleRefresh}
            />

            <SettingsActions
                isLoading={isActuallyLoading}
                multipliers={multipliers}
                editedMultipliers={editedMultipliers}
                setEditedMultipliers={setEditedMultipliers}
            />
        </>
    );
}
